import type { IntlShape, MessageDescriptor } from 'react-intl';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import type { ServiceExtraChipTone } from '../extras/serviceExtraTypes';
import { servicesPanelMessages as pm } from '../servicesPanelMessages';
import { shownLink } from '../serviceLinkEdit';
import type { SoundCloudExtraAction, SoundCloudExtraChip, SoundCloudExtraView } from './soundCloudExtraRules';
import { soundCloudExtrasMessages as m } from './soundCloudExtrasMessages';

export const SOUNDCLOUD_ACTION_LABELS: Record<SoundCloudExtraAction, MessageDescriptor> = {
  upload: m.upload,
  connect: pm.connect,
  reconnect: pm.reconnect,
  change: m.change,
  cancel: pm.cancel,
  status: pm.status,
  retry: pm.retry,
  open: pm.open,
  addMaster: m.addMaster,
};

/** The chip tones (design a1: `wt` violet, `by` green, `mk` grey, `bad` red). */
export function soundCloudChipTone(chip: SoundCloudExtraChip): ServiceExtraChipTone {
  switch (chip) {
    case 'onReleaseDay':
    case 'goesUp':
    case 'uploading':
      return 'work';
    case 'onSoundCloud':
      return 'ok';
    case 'private':
      return 'muted';
    case 'failed':
    case 'signedOut':
    case 'removed':
      return 'bad';
  }
}

function shortDate(intl: IntlShape, iso: string): string {
  return intl.formatDate(iso, { day: 'numeric', month: 'short' });
}

export function soundCloudChipText(
  intl: IntlShape,
  chip: SoundCloudExtraChip,
  publish: SerializedSongPublish | null
): string {
  switch (chip) {
    case 'onReleaseDay':
      return intl.formatMessage(m.chipOnReleaseDay);
    case 'goesUp':
      return intl.formatMessage(pm.chipGoesUp, {
        date: shortDate(intl, publish?.publishAt ?? publish?.createdAt ?? new Date().toISOString()),
      });
    case 'uploading':
      return intl.formatMessage(pm.chipUploading);
    case 'onSoundCloud':
      return intl.formatMessage(m.chipOnSoundCloud);
    case 'private':
      return intl.formatMessage(pm.chipPrivate);
    case 'failed':
      return intl.formatMessage(pm.chipFailed);
    case 'signedOut':
      return intl.formatMessage(pm.chipSignedOut);
    case 'removed':
      return intl.formatMessage(pm.chipRemoved);
  }
}

/** The state sentence of the "Song" item. */
export function soundCloudSentence(
  intl: IntlShape,
  view: SoundCloudExtraView,
  publish: SerializedSongPublish | null
): string {
  switch (view.state) {
    case 'noMaster':
      return intl.formatMessage(m.noMaster);
    case 'masterProcessing':
      return intl.formatMessage(m.masterProcessing);
    case 'connect':
      return intl.formatMessage(m.connect);
    case 'ready':
      return intl.formatMessage(m.ready);
    case 'waits': {
      const at = publish?.publishAt ?? null;
      return at === null
        ? intl.formatMessage(m.uploading)
        : intl.formatMessage(m.waitsReleaseDay, {
            date: intl.formatDate(at, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
            time: intl.formatTime(at, { hour: '2-digit', minute: '2-digit' }),
            sharing: publish?.sharing ?? 'public',
          });
    }
    case 'uploading':
      return intl.formatMessage(m.uploading);
    case 'live':
      return view.url === null ? intl.formatMessage(m.chipOnSoundCloud) : shownLink(view.url);
    case 'failed':
      return intl.formatMessage(pm.lineFailed);
    case 'unsure':
      return intl.formatMessage(m.unsure);
    case 'signedOut':
      return intl.formatMessage(pm.lineSignedOut);
    case 'removed':
      return intl.formatMessage(pm.lineRemoved);
  }
}

/** True when the sentence shows in the neg colour. */
export function soundCloudSentenceIsBad(view: SoundCloudExtraView): boolean {
  return view.state === 'failed' || view.state === 'unsure' || view.state === 'signedOut' || view.state === 'removed';
}
